import { Component, computed, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { ButtonModule } from '../button/button.module'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule, MatIconButton } from '@angular/material/button'
import { AssetCardComponent } from '../asset-card/asset-card.component'
import { Router, RouterModule } from '@angular/router'
import { EventService } from 'src/app/core/services/event.service'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { IAsset } from 'src/app/core/interfaces/asset.interface'
import { AssetMeterPipe } from 'src/app/core/pipes/asset-meter.pipe'
import { DefaultStringPipe } from 'src/app/core/pipes/default-string.pipe'
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet'
import { IComparisonModalOptions } from 'src/app/core/interfaces/modal.interface'
import { GlobalDataService } from 'src/app/core/services/global-data.service'
import { ComparisonService } from 'src/app/core/services/comparison.service'
import { ModalService } from 'src/app/core/services/modal.service'
import { IconButtonModule } from '../icon-button/icon-button.module'
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout'
import { toSignal } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-comparison-modal',
  template: `
    <div class="modal" data-testid="comparison-modal">
      <div class="header-bar"></div>
      <div class="header">
        <div class="header-content">
          <span class="title" i18n="@@comparison-modal.title">My List</span>
          <span class="count" i18n="@@comparison-modal.count">{{ comparisonService.assets().length }} Total</span>
        </div>
        <div class="header-actions">
          <app-button
            btn-responsive="medium"
            btn-secondary btn-outlined
            (click)="clearAll()"
            i18n="@@general.clear-all-button.label"
            class="btn-clear"
            data-testid="clear-all-button"
          >Clear all</app-button>
          <!-- <app-button
            btn-responsive="medium"
            btn-primary
            (click)="contactClicked()"
            i18n="@@general.contact-button.label"
            class="btn-contact"
            data-testid="contact-button"
          >Contact</app-button> -->
          <app-icon-button
            class="btn-collapse"
            icon-btn-outlined
            icon-btn-responsive="small"
            icon-btn-icon-responsive="large"
            icon-btn-secondary
            matIcon="expand_more"
            (click)="bottomSheetRef.dismiss()"
            i18n-aria-label="@@comparison.modal.hide-button.label"
            aria-label="Hide"
            data-testid="collapse-button"
          ></app-icon-button>
        </div>
      </div>
      <div class="content">
        <div
          class="content__container"
          data-testid="comparison-cards-container"
          [style.grid-template-columns]="'repeat(' + comparisonService.assets().length + ', ' + assetCardWidth() + ')'"
        >
          @for (asset of comparisonService.assets(); track $index) {
            <app-asset-card
              [asset]="asset"
              [showCarousel]="true"
              [comparisonMode]="true"
              [defaultImage]="globalDataService.defaultImage()"
              [currencyCode]="globalDataService.selectedCurrency().code"
              (imageClick)="imageClicked($event)"
              (detailsClick)="detailsClicked($event)"
              (contactClick)="contactClicked(asset)"
              data-testid="asset-card"
            ></app-asset-card>
            <div class="remove">
              <button mat-icon-button aria-label="Remove" i18n-aria-label="@@comparison-card.remove-button.aria-label" (click)="clearAsset(asset)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
            <div class="compare-item">
              <span class="compare-label" i18n="@@asset-details.location-label">Location</span>
              <span class="compare-value">
                {{
                  !!asset.branch_city
                    ? asset.branch_city + ', ' + asset.branch_state
                    : 'N/A'
                }}
              </span>
            </div>
            <div class="compare-item">
              <span class="compare-label" i18n="@@asset-details.meter-label">Meter</span>
              <span class="compare-value">{{asset | assetMeter: 'short' }}</span>
            </div>
            <div class="compare-item">
              <span class="compare-label" i18n="@@asset-details.serial-number-label">Serial #</span>
              <span class="compare-value">{{asset.serial_number | defaultString: '--' }}</span>
            </div>
            <div class="compare-item">
              <span class="compare-label" i18n="@@asset-details.equipment-number-label">Equipment #</span>
              <span class="compare-value">{{asset.display_equipment_number | defaultString: '--' }}</span>
            </div>
            <div class="compare-item">
              <span class="compare-label" i18n="@@asset-details.description-label">Comments from Seller</span>
              <span class="compare-value">{{ asset.detailed_description | defaultString: '--' }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  styles: [`
    @use "sass:map";
    @import "src/assets/_colors";
    @import 'src/assets/typography';

    @property --comparison-modal-bg-color {
      syntax: '<color>';
      inherits: true;
      initial-value: transparent;
    }

    @property --comparison-modal-label-text-color {
      syntax: '<color>';
      inherits: true;
      initial-value: map-get($light, text, secondary);
    }

    @property --comparison-modal-value-text-color {
      syntax: '<color>';
      inherits: true;
      initial-value: map-get($light, text, primary);
    }

    @property --comparison-modal-outline-color {
      syntax: '<color>';
      inherits: true;
      initial-value: map-get($light, other, outlined-border);
    }

    :host {
      width: 100%;
      background-color: var(--comparison-modal-bg-color);
      display: block;
    }

    button[mat-icon-button] {
      border: 1px solid var(--comparison-modal-outline-color);
    }

    .modal {
      height: 100%;
      display: flex;
      flex-direction: column;

      .header-bar {
        background-color: var(--theme-comparison-modal-header-bg-color);
        color: var(--theme-comparison-modal-header-font-color);
        height: 22px;
      }

      .header {
        position: relative;
        border-bottom: 1px solid var(--comparison-modal-outline-color);
        padding: 16px;
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        align-items: flex-start;
        justify-content: space-between;

        @include mq(medium) {
          align-items: center;
          flex-direction: row;
          height: 60px;
          padding: 32px;
        }

        .title {
          @include typography(h6);

          @include mq(medium) {
            @include typography(h4);
          }
        }
      }

      .header-content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        @include mq(medium) {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .count {
          @include typography(body-2);
          color: var(--comparison-modal-label-text-color);

          @include mq(medium) {
            @include typography(body-1);
            margin-left: 20px;
          }
        }
      }

      .header-actions {
        display: flex;
        flex-grow: 1;
        align-items: center;
        gap: 12px;
        margin-top: 12px;
        width: 100%;

        .btn-collapse {
          position: absolute;
          right: 16px;
          top: 20px;
        }

        .btn-clear,
        .btn-contact {
          --app-button-flex: 50%;
          --app-button-width: 100%;
          display: flex;

          button.Button {
            flex-grow: 1;
          }
        }

        @include mq(medium) {
          margin-top: 0;
          flex-grow: 0;
          gap: 32px;
          width: auto;

          .btn-contact {
            display: none;
          }

          .btn-clear,
          .btn-contact {
            --app-button-flex: auto;
            --app-button-width: auto;
          }

          .btn-collapse {
            position: static
          }
        }
      }

      .content {
        overflow-x: auto;
        &__container {
          padding: 20px 16px 0;
          overflow-x: auto;
          display: grid;
          grid-auto-flow: column;
          gap: 32px;
          grid-template-rows: repeat(7, auto);

          @include mq(large) {
            padding: 32px;
          }

          .compare-item {
            padding: 0 12px 0 0;
            border-bottom: 1px solid var(--comparison-modal-outline-color);
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            .compare-label {
              @include typography(body-2);
              color: var(--comparison-modal-label-text-color);
            }

            .compare-value {
              @include typography(subtitle-2-medium);
              color: var(--comparison-modal-value-text-color);
            }
          }

          .remove {
            margin-top: -12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  `],
  imports: [
    MatIconModule,
    MatButtonModule,
    MatIconButton,
    CommonModule,
    ButtonModule,
    IconButtonModule,
    AssetCardComponent,
    RouterModule,
    DefaultStringPipe,
    AssetMeterPipe,
  ],
})
export class ComparisonModalComponent {
  data = inject<IComparisonModalOptions>(MAT_BOTTOM_SHEET_DATA)

  protected modalService = inject(ModalService)
  protected globalDataService = inject(GlobalDataService)
  protected comparisonService = inject(ComparisonService)
  protected bottomSheetRef = inject<MatBottomSheetRef<ComparisonModalComponent>>(MatBottomSheetRef)
  private _eventService = inject(EventService)
  private _router = inject(Router)
  private _breakpointObserver = inject(BreakpointObserver)

  // Simple responsive width using toSignal
  isSmallScreen = toSignal(this._breakpointObserver.observe(Breakpoints.XSmall))
  assetCardWidth = computed(() => this.isSmallScreen()?.matches ? '304px' : '324px')

  imageClicked({ asset, images }: { asset: IAsset, images: Array<{src: string, thumb: string}> }) {
    this.modalService.gallery({
      title: `${asset.year} ${asset.make} ${asset.model}`,
      images: images,
    })
  }

  detailsClicked(assetId: string) {
    this.bottomSheetRef.dismiss()
    this._router.navigate([RoutePaths.Details, assetId])
  }

  contactClicked(asset?: IAsset) {
    this._eventService.notify('contactClicked', asset)
  }

  clearAll() {
    this.bottomSheetRef.dismiss()
    this._eventService.notifyClearAllComparisonAssets()
  }

  clearAsset(asset: IAsset) {
    const MINIMUM_ASSETS = 2
    if (this.comparisonService.assets().length === MINIMUM_ASSETS) {
      this.bottomSheetRef.dismiss()
    }

    this._eventService.notifyClearComparisonAsset(asset.equipment_number)
  }
}
